import {all, call, put, select, take, takeLatest, takeLeading} from 'redux-saga/effects'
import noop from 'lodash/noop'

import history from '../history'

import {FieldValidationErrors} from '../exceptions'
import {callIfNotExist, throwBadResponseError} from './helpers'
import {clickedLink} from '../track/other'
import {
  dashboardEventDetailChanged,
  dashboardEventNumMembersChanged,
  dashboardEventOwnerRoleChanged,
} from '../track/dashboard'
import * as trackCoreg from '../track/coregistration'
import * as api from '../api/api'
import {onStatus} from '../api/helpers'
import * as eventsAndLooksApi from '../api/eventAndLooks'
import {openDashboard, leaveEventDetailEditMode} from '../actions/dashboard'
import {GET_EVENT_AND_ORDER_DETAILS} from '../actions/eventMgr'
import {
  ADD_EVENT,
  CHANGE_CURRENT_EVENT,
  CHANGE_CURRENT_EVENT_FAILURE,
  CHANGE_CURRENT_EVENT_SUCCESS,
  CREATE_NEW_EVENT,
  DELETE_EVENT,
  EDIT_IN_PROGRESS_EVENT,
  GET_ALL_EVENTS,
  GET_ALL_EVENTS_FAILURE,
  GET_ALL_EVENTS_SUCCESS,
  SUBMIT_EVENT_DETAIL_CHANGE_VIA_DASHBOARD,
  SUBMIT_EVENT_NUM_MEMBERS_CHANGE_VIA_DASHBOARD,
  SUBMIT_EVENT_OWNER_ROLE_CHANGE_VIA_DASHBOARD,
  UPDATE_PARTNER_INFO,
  addCurrentEventToAllEvents,
  changeCurrentEvent,
  changeCurrentEventCompleted,
  changeCurrentEventFailure,
  changeCurrentEventPending,
  changeCurrentEventSuccess,
  clearCurrentEvent,
  createNewEvent,
  createNewEventFailure,
  createNewEventSuccess,
  deleteEventFailure,
  deleteEventSuccess,
  getAllEvents,
  getAllEventsFailure,
  getAllEventsPending,
  getAllEventsSuccess,
  updateCurrentEvent,
  updateCurrentNumMembers,
  updateCurrentOwnerRole,
  updatePartnerInfoFailure,
  updatePartnerInfoPending,
  updatePartnerInfoSuccess,
} from '../actions/events'
import {
  GET_EVENT_LOOKS_FAILURE,
  GET_EVENT_LOOKS_SUCCESS,
  GET_SOONEST_EVENT_DATE,
  getSoonestEventDateFailure,
  getSoonestEventDatePending,
  getSoonestEventDateSuccess,
  getEventLooks,
} from '../actions/api'
import {
  addLook,
  GET_PARTICIPANT_LOOK_SUCCESS,
  getParticipantLook,
  updateCurrentLooks,
} from '../actions/looks'
import {initiateOnboarding} from '../components/Onboarding/redux/actions'
import {isBadResponse} from '../api/statusCodes'
import {updateCurrentPeople} from '../actions/people'
import {openErrorModal} from '../actions/userInteractions'
import * as cross from '../store/cross'
import * as events from '../store/events'
import * as user from '../store/user'
import {forUserAndEvent} from './login'
import * as URLS from '../urls'

// Add Event From My Events Page
// ----------------------------------------------------------------------------

export function* addEventWorker() {
  const currentEventIsValid = yield select(events.currentEventIsValid)
  const currentEventIsInAll = yield select(events.currentEventIsInAll)
  if (currentEventIsValid) {
    yield put(addCurrentEventToAllEvents({currentEventIsInAll}))
  }
  yield put(clearCurrentEvent())
  yield put(createNewEvent())
}

export function* addEventSaga() {
  yield takeLatest(ADD_EVENT, addEventWorker)
}

// Change Current Event
// ----------------------------------------------------------------------------

export function* changeCurrentEventWorker({payload: {eventId}}) {
  const eventData = yield select(events.eventById, eventId)
  try {
    const response = yield call(api.changeCurrentEvent, eventId)
    onStatus({
      OK: noop,
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not change current event: ${statusText}`)
      },
    })(response)
    yield put(changeCurrentEventSuccess(eventData))
  } catch (err) {
    yield put(changeCurrentEventFailure(err))
  }
}

export function* changeCurrentEventSaga() {
  yield takeLatest(CHANGE_CURRENT_EVENT, changeCurrentEventWorker)
}

// Create New Event
// ----------------------------------------------------------------------------

export function* createNewEventWorker({payload: {callback}}) {
  try {
    const response = yield call(api.createNewEvent)
    const result = onStatus({
      OK: ({json}) => json,
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not create new event: ${statusText}`)
      },
    })(response)
    yield put(createNewEventSuccess(result))
    yield put(initiateOnboarding(callback))
  } catch (err) {
    yield put(createNewEventFailure(err))
    yield put(openErrorModal())
  }
}

export function* createNewEventSaga() {
  yield takeLatest(CREATE_NEW_EVENT, createNewEventWorker)
}

// Delete Event
// ----------------------------------------------------------------------------

export function* deleteEventWorker({payload: {eventId}}) {
  const isCurrentEvent = yield select(events.isCurrentEvent, eventId)
  const userId = yield select(user.id)
  try {
    const response = yield call(api.deleteEvent, eventId, userId)
    const returnedEvent = onStatus({
      OK: ({json}) => json,
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not delete event: ${statusText}`)
      },
    })(response)
    // returns most recently accessed event data after deleting event
    // this becomes new current event if current event was deleted
    if (isCurrentEvent) {
      yield put(updateCurrentEvent(returnedEvent.currentEvent))
      yield put(updateCurrentLooks(returnedEvent.looksAndPeople.looks))
      yield put(updateCurrentPeople(returnedEvent.looksAndPeople.people))
    }
    // remove deleted event from stack
    yield put(deleteEventSuccess(eventId))
    yield call(clickedLink, {location: 'My Events Page', text: 'Delete', type: 'button'})
  } catch (err) {
    yield put(deleteEventFailure())
    yield put(openErrorModal())
  }
}

export function* deleteEventSaga() {
  yield takeLatest(DELETE_EVENT, deleteEventWorker)
}

// Edit In Progress Event
// ----------------------------------------------------------------------------

export function* checkForEventDetails(eventId) {
  const hasEventDetailsById = yield select(events.hasEventDetailsById, eventId)
  const userIsParticipant = yield select(cross.userIsParticipant)

  // if event is missing details, we need to prompt the user to add them via Onboarding
  if (!hasEventDetailsById) {
    yield put(initiateOnboarding())
    return
  }

  if (userIsParticipant) {
    yield call(history.push, URLS.EVENT_SUMMARY)
  } else {
    yield put(openDashboard())
  }
}

export function* changeCurrentEventLooksWorker(eventId) {
  const userIsOwner = yield select(cross.userIsOwnerByEventId, eventId)
  if (!userIsOwner) {
    yield put(getParticipantLook())
    yield take(GET_PARTICIPANT_LOOK_SUCCESS)
    return
  }

  yield put(getEventLooks(eventId))
  const {type} = yield take([GET_EVENT_LOOKS_FAILURE, GET_EVENT_LOOKS_SUCCESS])

  if (type === GET_EVENT_LOOKS_FAILURE) {
    throw new Error('Could not get the event looks')
  }
}

export function* changeCurrentEventDetailsWorker(eventId) {
  yield put(changeCurrentEvent(eventId))
  const {type} = yield take([CHANGE_CURRENT_EVENT_FAILURE, CHANGE_CURRENT_EVENT_SUCCESS])

  if (type === CHANGE_CURRENT_EVENT_FAILURE) {
    throw new Error('Could not change the current event')
  }
}

/**
 * If the user selects another in-progress event, load that data. Then, direct
 * them to the appropriate screen via `checkForEventDetails`
 */
export function* editInProgressEventWorker({payload: {eventId}}) {
  const isCurrentEvent = yield select(events.isCurrentEvent, eventId)
  const currentEventIsInAll = yield select(events.currentEventIsInAll)
  try {
    yield put(addCurrentEventToAllEvents({currentEventIsInAll}))
    if (!isCurrentEvent) {
      yield put(changeCurrentEventPending())
      yield call(changeCurrentEventDetailsWorker, eventId)
      yield call(changeCurrentEventLooksWorker, eventId)
      yield put(changeCurrentEventCompleted())
    }
    yield call(checkForEventDetails, eventId)
  } catch {
    yield put(openErrorModal())
  }
}

export function* editInProgressEventSaga() {
  yield takeLatest(EDIT_IN_PROGRESS_EVENT, editInProgressEventWorker)
}

// Get All Events
// ----------------------------------------------------------------------------

export const getAllEventsIfNotExist = callIfNotExist(
  events.allEventsAreFetched,
  getAllEvents,
  GET_ALL_EVENTS_FAILURE,
  GET_ALL_EVENTS_SUCCESS,
  'Could not get all events.',
)

export function* getAllEventsWorker() {
  try {
    yield forUserAndEvent()
    const userId = yield select(user.id)
    if (!userId) {
      throw new Error('Failure to retrieve userId.')
    }

    yield put(getAllEventsPending())

    const response = yield call(api.getAllEvents, userId)
    const payload = onStatus({
      OK: ({json}) => json,
      DEFAULT: throwBadResponseError,
    })(response)

    yield put(getAllEventsSuccess(payload))
  } catch (err) {
    yield put(getAllEventsFailure(err))
  }
}

export function* getAllEventsSaga() {
  yield takeLeading([GET_ALL_EVENTS, GET_EVENT_AND_ORDER_DETAILS], getAllEventsWorker)
}

// Get Soonest Event Date
// ----------------------------------------------------------------------------

export function* getSoonestEventDateWorker() {
  try {
    yield put(getSoonestEventDatePending())

    const {json, status} = yield call(api.getSoonestEventDate)

    if (isBadResponse(status)) {
      throw new Error('Could not get soonest event date. Unexpected error.')
    }

    yield put(getSoonestEventDateSuccess(json))
  } catch (e) {
    yield put(getSoonestEventDateFailure(e))
  }
}

export function* getSoonestEventDateSaga() {
  yield takeLatest(GET_SOONEST_EVENT_DATE, getSoonestEventDateWorker)
}

// Submit Event Detail Change Via Dashboard
// ----------------------------------------------------------------------------

function* handleEventDateChange(data) {
  yield all(
    data.eventLooks.map((look) => {
      // sort through looks, check for hto
      if (look.isHto) {
        return put(addLook(look))
      }
      return look
    }),
  )
}

/**
 * @param {Object} action
 * @param {Object} action.payload
 * @param {Object} action.payload.detail The data to update with
 * @param {string} action.payload.detailType Which detail we are editing (Date|Event Type|Name)
 */
export function* submitEventDetailChangeViaDashboardWorker({payload: {detail, detailType}}) {
  const eventId = yield select(events.currentId)
  try {
    const response = yield call(api.postEventDetails, eventId, detail)
    const data = onStatus({
      OK: ({json}) => json,
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not submit event ${detailType} change: ${statusText}`)
      },
    })(response)

    // changing the event date may affect event look data, we need to do extra handling
    if (detailType === 'Date') {
      yield call(handleEventDateChange, data)
    }

    yield put(
      updateCurrentEvent({
        date: data.date,
        name: data.name,
        type: data.eventType,
      }),
    )
    yield call(dashboardEventDetailChanged, detail, detailType)
    yield put(leaveEventDetailEditMode())
  } catch (e) {
    yield call(console.error, `Could not save new event ${detailType}. Reason: ${e.message}`)
  }
}

export function* submitEventDetailChangeViaDashboardSaga() {
  yield takeLatest(
    SUBMIT_EVENT_DETAIL_CHANGE_VIA_DASHBOARD,
    submitEventDetailChangeViaDashboardWorker,
  )
}

// Submit Event Num Members Change Via Dashboard
// ----------------------------------------------------------------------------

export function* submitEventNumMembersChangeViaDashboardWorker({payload}) {
  const eventId = yield select(events.currentId)
  try {
    const response = yield call(api.postEventMetaData, eventId, payload)
    const newNumMembers = onStatus({
      OK: ({json}) => json.number_of_renters,
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not submit event meta data change: ${statusText}`)
      },
    })(response)
    yield put(updateCurrentNumMembers(newNumMembers))
    yield call(dashboardEventNumMembersChanged, newNumMembers)
    yield put(leaveEventDetailEditMode())
  } catch (e) {
    yield call(console.error, `Could not save new event meta data. Reason: ${e.message}`)
  }
}

export function* submitEventNumMembersChangeViaDashboardSaga() {
  yield takeLatest(
    SUBMIT_EVENT_NUM_MEMBERS_CHANGE_VIA_DASHBOARD,
    submitEventNumMembersChangeViaDashboardWorker,
  )
}

// Submit Event Role Change Via Dashboard
// ----------------------------------------------------------------------------

export function* submitEventOwnerRoleChangeViaDashboardWorker({payload}) {
  const eventId = yield select(events.currentId)
  try {
    const response = yield call(api.postOwnerDetails, eventId, payload)
    const newRole = onStatus({
      OK: ({json}) => json.role,
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not submit event role change: ${statusText}`)
      },
    })(response)
    yield put(updateCurrentOwnerRole(newRole))
    yield call(dashboardEventOwnerRoleChanged, newRole)
    yield put(leaveEventDetailEditMode())
  } catch (e) {
    yield call(console.error, `Could not save new event role. Reason: ${e.message}`)
  }
}

export function* submitEventOwnerRoleChangeViaDashboardSaga() {
  yield takeLatest(
    SUBMIT_EVENT_OWNER_ROLE_CHANGE_VIA_DASHBOARD,
    submitEventOwnerRoleChangeViaDashboardWorker,
  )
}

export function* updatePartnerInfoWorker({
  payload: {email, eventId, firstName, isEmailOptedIn, lastName, origin},
}) {
  yield put(updatePartnerInfoPending())
  try {
    const currentEventId = yield select(events.currentId)
    const eventIdToUpdate = eventId || currentEventId
    const response = yield call(eventsAndLooksApi.updatePartnerInfo, eventIdToUpdate, {
      partner_email: email,
      partner_first_name: firstName,
      partner_last_name: lastName,
      partner_email_opt_in: isEmailOptedIn,
    })
    const result = onStatus({
      OK: ({json}) => json,
      400: ({json: errorsByField}) => {
        throw new FieldValidationErrors(errorsByField)
      },
      DEFAULT: () => {
        throw new Error('Could not update partner information. Unexpected error.')
      },
    })(response)

    yield put(updatePartnerInfoSuccess(result))
    yield call(trackCoreg.savedPartnerInfo, origin)
  } catch (err) {
    yield put(updatePartnerInfoFailure(err))
  }
}

export function* updatePartnerInfoSaga() {
  yield takeLatest(UPDATE_PARTNER_INFO, updatePartnerInfoWorker)
}
